import { MailOutlined, PhoneOutlined, UserOutlined } from "@ant-design/icons"
import {
  Avatar,
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Row,
  Segmented,
  Skeleton,
  Tag,
  Typography
} from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { empty } from "../../../constants/placeholders.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { useDateFormat } from "../../../hooks/use-date-format.ts"
import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import * as services from "../api/services.ts"
import { asArray, asList } from "../helpers/list-values.ts"
import {
  COMPLIANCE_DOCUMENT_LABELS,
  COMPLIANCE_DOCUMENT_ORDER,
  COMPLIANCE_STATUS_COLOR_MAP,
  TRACKED_COMPLIANCE_DOCUMENT_TYPES
} from "../constants/compliance.ts"
import { ComplianceDocumentUploadModal } from "../components/ComplianceDocumentUploadModal.tsx"
import { SubcontractorFormModal } from "../components/SubcontractorForm/SubcontractorFormModal.tsx"
import { type SubcontractorFormData } from "../components/SubcontractorForm/subcontractor-form-data.ts"
import {
  subcontractorPortalAccessLabels,
  subcontractorStatusColorMap
} from "../constants/suctontractor-statuses.ts"
import { updateSubcontractor } from "../redux/subcontractors.thunks.ts"
import {
  type SubcontractorComplianceDocumentType,
  type SubcontractorComplianceState,
  type SubcontractorListItem
} from "../types/types.ts"

const { Title, Text } = Typography

type ProjectsMapView = "active" | "past"

const getInitials = (name: string) => {
  const [first, second] = name.trim().split(/\s+/).filter(Boolean)
  return `${first?.[0] ?? ""}${second?.[0] ?? ""}`.toUpperCase() || "?"
}

// A compact, read-only label/value pair — the building block for the three
// new EAD-1322 detail sections (Identity & Tax, CSLB License, Payment).
// Always renders, falling back to the shared empty placeholder, so a
// section's shape never shifts depending on what the record happens to have.
const DetailField = ({
  label,
  value
}: {
  label: string
  value?: React.ReactNode
}) => (
  <Col xs={24} md={12}>
    <Flex vertical gap={2}>
      <Text type={"secondary"} className={"text-xs"}>
        {label}
      </Text>
      <Text>{value || empty}</Text>
    </Flex>
  </Col>
)

const SubcontractorDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const [subcontractor, setSubcontractor] =
    useState<SubcontractorListItem | null>(null)
  const [fetching, setFetching] = useState(true)
  // Only a 404 means the record is gone; a 500 or a dropped connection is a
  // failure to load, which the user can retry.
  const [loadError, setLoadError] = useState<"notFound" | "failed" | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const { t } = useTranslation()
  const { parseDate, userFormat } = useDateFormat()
  const [projectsMapView, setProjectsMapView] =
    useState<ProjectsMapView>("active")
  const [editing, setEditing] = useState(false)
  const [uploadingType, setUploadingType] =
    useState<SubcontractorComplianceDocumentType | null>(null)
  const dispatch = useAppDispatch()
  const permissions = useAppSelector(
    (state) => state?.auth?.permissions?.codes?.subcontractors
  )
  const editAllowed = permissions?.subcontractors?.subcontractor_edit

  useEffect(() => {
    generateDocumentTitle("Subcontractor")
  }, [])

  useEffect(() => {
    if (!id) return

    let cancelled = false
    // Never show the previous record while another one loads.
    setSubcontractor(null)
    setFetching(true)
    setLoadError(null)

    services.getSubcontractorDetails(id).then((response) => {
      if (cancelled) return

      if (!response.ok) {
        setLoadError(response.status === 404 ? "notFound" : "failed")
        setFetching(false)
        return
      }

      setSubcontractor(response.data as SubcontractorListItem)
      setFetching(false)
    })

    return () => {
      cancelled = true
    }
  }, [id, reloadKey])

  const handleSave = async ({
    send_invitation: _sendInvitation,
    ...data
  }: SubcontractorFormData) => {
    if (!id) return

    const result = await dispatch(updateSubcontractor({ ...data, id }))
    if (!updateSubcontractor.fulfilled.match(result)) return

    // The PATCH response omits the derived detail fields (compliance status,
    // counts, project types), so re-read the record rather than merging.
    const refreshed = await services.getSubcontractorDetails(id)
    if (refreshed.ok) setSubcontractor(refreshed.data as SubcontractorListItem)
    setEditing(false)
  }

  // A single new/replaced document changes several derived fields at once
  // (its own status, the sidebar badge, and — for License — cslb_expiration_date),
  // so a full re-read is simpler and safer than reconciling one row by hand.
  const handleDocumentUploaded = () => {
    setUploadingType(null)
    setReloadKey((n) => n + 1)
  }

  if (fetching && !subcontractor) {
    return (
      <Card size={"small"}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    )
  }

  if (loadError === "failed") {
    return (
      <Card size={"small"}>
        <Empty description={t("subcontractorDetail.loadError")}>
          <Button onClick={() => setReloadKey((n) => n + 1)}>
            {t("subcontractorDetail.retry")}
          </Button>
        </Empty>
      </Card>
    )
  }

  if (!subcontractor || loadError === "notFound") {
    return (
      <Card size={"small"}>
        <Empty description={t("subcontractorDetail.notFound")} />
      </Card>
    )
  }

  const normalizedStatus =
    subcontractor.status?.toLowerCase() as keyof typeof subcontractorStatusColorMap
  // A subcontractor's profile is editable by any GC holding subcontractor_edit
  // regardless of status — Active and Suspended included. There is no
  // subcontractor-facing edit surface to collide with (unlike a Customer's
  // own portal), so nothing is protected by locking it once they sign in.
  const isEditable = !!editAllowed
  const complianceStatus = subcontractor.compliance_status || {}
  // The tracked types always render as a row — missing or not — so an admin
  // sees exactly what still needs attention; everything else the
  // subcontractor supplied (W-9, Miscellaneous) rides along, data-driven as
  // before.
  const complianceDocumentTypes = Array.from(
    new Set([
      ...TRACKED_COMPLIANCE_DOCUMENT_TYPES,
      ...Object.keys(complianceStatus)
    ])
  ).sort(
    (a, b) =>
      COMPLIANCE_DOCUMENT_ORDER.indexOf(a) -
      COMPLIANCE_DOCUMENT_ORDER.indexOf(b)
  )
  const complianceEntries = complianceDocumentTypes.map((docType) => [
    docType,
    complianceStatus[
      docType as keyof typeof complianceStatus
    ] as
      | { status: SubcontractorComplianceState; expiration_date: string | null }
      | undefined
  ]) as Array<
    [
      string,
      { status: SubcontractorComplianceState; expiration_date: string | null } | undefined
    ]
  >
  const documentByType = (docType: string) =>
    subcontractor.compliance_documents?.find((d) => d.document_type === docType)

  // EAD-1322: "business name" prefers the legal entity name a company
  // subcontractor supplied; falling back to the person's own name covers the
  // common case of an individual with no separate business identity.
  // "Primary contact" mirrors the same fallback so a solo subcontractor
  // isn't shown as their own contact twice — the name row only renders when
  // there's a distinct contact to name.
  const businessName = subcontractor.legal_business_name || getEntityName(subcontractor)
  const contactName = subcontractor.primary_contact_name
  const contactEmail = subcontractor.primary_contact_email || subcontractor.email
  const contactPhone = subcontractor.primary_contact_phone || subcontractor.phone
  const trades = asArray(subcontractor.specialization)
  const taxIdLabel = subcontractor.ein_last4 ? "EIN" : subcontractor.ssn_last4 ? "SSN" : "Tax ID"
  const taxIdValue = subcontractor.ein_last4
    ? `On file, ending ${subcontractor.ein_last4}`
    : subcontractor.ssn_last4
      ? `On file, ending ${subcontractor.ssn_last4}`
      : undefined

  return (
    <Flex vertical gap={16}>
      <Card size={"small"}>
        <Flex justify={"space-between"} align={"flex-start"} wrap gap={16}>
          <Flex gap={12} align={"center"}>
            <Avatar size={48} className={"shrink-0"}>
              {getInitials(businessName)}
            </Avatar>
            <Flex vertical gap={4}>
              <Title level={4} className={"!mb-0"}>
                {businessName}
              </Title>
              <Flex gap={16} wrap align={"center"}>
                {contactName && (
                  <Flex gap={4} align={"center"}>
                    <UserOutlined className={"text-gray-400"} />
                    <Text type={"secondary"}>{contactName}</Text>
                  </Flex>
                )}
                <Flex gap={4} align={"center"}>
                  <MailOutlined className={"text-gray-400"} />
                  <Text type={"secondary"}>{contactEmail || empty}</Text>
                </Flex>
                <Flex gap={4} align={"center"}>
                  <PhoneOutlined className={"text-gray-400"} />
                  <Text type={"secondary"}>{contactPhone || empty}</Text>
                </Flex>
              </Flex>
              {trades.length > 0 && (
                <Flex gap={8} wrap className={"mt-1"}>
                  {trades.map((trade) => (
                    <Tag key={trade}>{trade}</Tag>
                  ))}
                </Flex>
              )}
            </Flex>
          </Flex>
          <Flex vertical align={"flex-end"} gap={8}>
            <Flex gap={6} align={"center"}>
              <Text type={"secondary"}>
                {t("subcontractorDetail.portalAccess")}:
              </Text>
              <Tag
                data-testid={"portal-access-tag"}
                color={subcontractorStatusColorMap[normalizedStatus] ?? "default"}
              >
                {subcontractorPortalAccessLabels[normalizedStatus] ??
                  subcontractor.status ??
                  empty}
              </Tag>
            </Flex>
            {isEditable && (
              <Button type={"primary"} onClick={() => setEditing(true)}>
                {t("subcontractorDetail.edit")}
              </Button>
            )}
          </Flex>
        </Flex>
      </Card>

      {editing && (
        <SubcontractorFormModal
          onSave={handleSave}
          onCancel={() => setEditing(false)}
          // The API's nullable columns come back as null; the form models them as
          // absent, which antd treats identically.
          initialValues={subcontractor as Partial<SubcontractorFormData>}
        />
      )}

      <Flex gap={16} wrap>
        <Card
          size={"small"}
          title={t("subcontractorDetail.totalActiveProjects")}
          className={"flex-1 min-w-[200px]"}
        >
          <Text className={"text-2xl font-semibold"}>
            {subcontractor.active_assignments_count ?? 0}
          </Text>
        </Card>
        <Card
          size={"small"}
          title={t("subcontractorDetail.totalProjects")}
          className={"flex-1 min-w-[200px]"}
        >
          <Text className={"text-2xl font-semibold"}>
            {subcontractor.assignments_count ?? 0}
          </Text>
        </Card>
      </Flex>

      <Card size={"small"} title={t("subcontractorDetail.projectTypes")}>
        {subcontractor.project_types?.length ? (
          <Flex gap={8} wrap>
            {subcontractor.project_types.map((type) => (
              <Tag key={type}>{type}</Tag>
            ))}
          </Flex>
        ) : (
          <Text type={"secondary"}>{empty}</Text>
        )}
      </Card>

      {/* EAD-1322 (closes EAD-1296): identity/tax/CSLB/payment were visible
          only inside the Edit modal. Read-only here, always shown — secrets
          stay masked to their last 4 digits, never revealed on this page. */}
      <Card size={"small"} title={t("subcontractorDetail.identityAndTax")}>
        <Row gutter={[12, 12]}>
          <DetailField label={"Legal Business Name"} value={subcontractor.legal_business_name} />
          <DetailField label={"Entity Type"} value={subcontractor.entity_type_display} />
          <DetailField label={"Business Address"} value={subcontractor.business_address} />
          <DetailField label={"City"} value={subcontractor.business_city} />
          <DetailField label={"State"} value={subcontractor.business_state} />
          <DetailField label={"ZIP Code"} value={subcontractor.business_zip_code} />
          <DetailField label={taxIdLabel} value={taxIdValue} />
          <DetailField
            label={"W-9 Document"}
            value={subcontractor.w9_document ? "On file" : undefined}
          />
          <DetailField label={"W-9 Validity Date"} value={subcontractor.w9_validity_date} />
        </Row>
      </Card>

      <Card size={"small"} title={t("subcontractorDetail.cslbLicense")}>
        <Row gutter={[12, 12]}>
          <DetailField label={"License Number"} value={subcontractor.cslb_license_number} />
          <DetailField
            label={"Classification"}
            value={asList(subcontractor.cslb_classification)}
          />
          <DetailField label={"Status"} value={subcontractor.cslb_status_display} />
          <DetailField label={"Issue Date"} value={subcontractor.cslb_issue_date} />
          <DetailField label={"Bond Amount"} value={subcontractor.cslb_bond_amount} />
          <DetailField label={"Bond Number"} value={subcontractor.cslb_bond_number} />
          <DetailField
            label={"Qualifying Individual Bond Amount"}
            value={subcontractor.cslb_qualifier_bond_amount}
          />
          <DetailField
            label={"Qualifying Individual Bond Number"}
            value={subcontractor.cslb_qualifier_bond_number}
          />
        </Row>
      </Card>

      <Card size={"small"} title={t("subcontractorDetail.payment")}>
        <Row gutter={[12, 12]}>
          <DetailField label={"Payment Method"} value={subcontractor.payment_method_display} />
          {subcontractor.payment_method === "ach" && (
            <>
              <DetailField
                label={"ACH Routing Number"}
                value={
                  subcontractor.ach_routing_number_last4
                    ? `On file, ending ${subcontractor.ach_routing_number_last4}`
                    : undefined
                }
              />
              <DetailField
                label={"ACH Account Number"}
                value={
                  subcontractor.ach_account_number_last4
                    ? `On file, ending ${subcontractor.ach_account_number_last4}`
                    : undefined
                }
              />
            </>
          )}
        </Row>
      </Card>

      <Card size={"small"} title={t("subcontractorDetail.complianceDocuments")}>
        <Flex vertical gap={8}>
          {complianceEntries.map(([docType, compliance]) => {
            const tracked = TRACKED_COMPLIANCE_DOCUMENT_TYPES.includes(
              docType as SubcontractorComplianceDocumentType
            )
            const document = tracked ? documentByType(docType) : undefined

            return (
              <Flex key={docType} justify={"space-between"} align={"center"} wrap gap={8}>
                <Text>{COMPLIANCE_DOCUMENT_LABELS[docType] || docType}</Text>
                <Flex gap={8} align={"center"} wrap>
                  {compliance ? (
                    <>
                      <Text type={"secondary"}>
                        {parseDate(compliance.expiration_date)?.format(
                          userFormat || "YYYY-MM-DD"
                        ) ?? empty}
                      </Text>
                      <Tag
                        color={
                          COMPLIANCE_STATUS_COLOR_MAP[compliance.status] ?? "default"
                        }
                      >
                        {t(`subcontractorCompliance.status.${compliance.status}`, {
                          defaultValue: compliance.status
                        })}
                      </Tag>
                    </>
                  ) : (
                    <Tag>{t("subcontractorDetail.compliance.missing")}</Tag>
                  )}
                  {tracked && isEditable && (
                    <>
                      {document?.file && (
                        <a href={document.file} target={"_blank"} rel={"noreferrer"}>
                          {t("subcontractorDetail.compliance.view")}
                        </a>
                      )}
                      <Button
                        size={"small"}
                        onClick={() =>
                          setUploadingType(
                            docType as SubcontractorComplianceDocumentType
                          )
                        }
                      >
                        {document
                          ? t("subcontractorDetail.compliance.replace")
                          : t("subcontractorDetail.compliance.upload")}
                      </Button>
                    </>
                  )}
                </Flex>
              </Flex>
            )
          })}
        </Flex>
      </Card>

      {uploadingType && (
        <ComplianceDocumentUploadModal
          open
          subcontractorId={subcontractor.id}
          documentType={uploadingType}
          currentExpirationDate={documentByType(uploadingType)?.expiration_date}
          onClose={() => setUploadingType(null)}
          onUploaded={handleDocumentUploaded}
        />
      )}

      <Card
        size={"small"}
        title={t("subcontractorDetail.projectsMap")}
        extra={
          <Segmented
            size={"small"}
            value={projectsMapView}
            onChange={(value) => setProjectsMapView(value as ProjectsMapView)}
            options={[
              { label: t("subcontractorDetail.active"), value: "active" },
              { label: t("subcontractorDetail.past"), value: "past" }
            ]}
          />
        }
      >
        <Flex
          align={"center"}
          justify={"center"}
          className={"h-64 rounded bg-gray-50"}
        >
          <Text type={"secondary"}>
            {projectsMapView === "active"
              ? t("subcontractorDetail.activeMapSoon")
              : t("subcontractorDetail.pastMapSoon")}
          </Text>
        </Flex>
      </Card>
    </Flex>
  )
}

export default SubcontractorDetailPage
